import { Command as CommandPrimitive } from 'cmdk'
import { X } from 'lucide-react'
import { useMemo, useRef, useState } from 'react'
import type { KeyboardEvent } from 'react'

import { Badge } from '@/components/ui/badge'
import { Command, CommandGroup, CommandItem } from '@/components/ui/command'
import { Icons } from '@/components/ui/icons'
import { toast } from '@/components/ui/use-toast'
import {
  useMutationCreateSegment,
  useQuerySegments
} from '@/services/segment.service'
import {
  addSegmentValue,
  filterSegments,
  getConflictSegment,
  getSegmentErrorMessage,
  removeSegmentValue,
  sanitizeSegmentInput,
  shouldShowCreateSegment
} from '@/utils/segments'

interface SegmentsInputProps {
  value: string[]
  onChange: (values: string[]) => void
  disabled: boolean
}

export const SegmentsInput = ({
  value,
  onChange,
  disabled
}: SegmentsInputProps) => {
  const inputRef = useRef<HTMLInputElement>(null)
  const [open, setOpen] = useState(false)
  const [inputValue, setInputValue] = useState('')
  const { data: segments = [], isLoading } = useQuerySegments()
  const createSegment = useMutationCreateSegment()

  const matchingSegments = useMemo(
    () => filterSegments(segments, value, inputValue),
    [inputValue, segments, value]
  )
  const showCreateSegment =
    !isLoading && shouldShowCreateSegment(inputValue, matchingSegments)

  const handleCreateSegment = () => {
    if (createSegment.isPending) return

    const label = inputValue.trim()

    createSegment.mutate(label, {
      onSuccess: (segment) => {
        onChange(addSegmentValue(value, segment.value))
        setInputValue('')
      },
      onError: (error) => {
        const existingSegment = getConflictSegment(error)

        if (existingSegment) {
          onChange(addSegmentValue(value, existingSegment.value))
          setInputValue('')
          toast({
            title: 'Segment already exists',
            description: `Selected existing segment ${existingSegment.label}`
          })

          return
        }

        toast({
          title: 'Unable to create segment',
          description: getSegmentErrorMessage(error),
          variant: 'destructive'
        })
      }
    })
  }

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (
      (event.key === 'Backspace' || event.key === 'Delete') &&
      inputValue === '' &&
      value.length > 0
    ) {
      onChange(removeSegmentValue(value, value.at(-1)!))
    }

    if (event.key === 'Escape') {
      inputRef.current?.blur()
    }
  }

  return (
    <Command
      className="overflow-visible bg-transparent"
      onKeyDown={handleKeyDown}
      shouldFilter={false}
    >
      <div className="px-1 py-2 h-full text-sm border border-border rounded-md group ring-offset-background focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2">
        <div className="flex flex-wrap gap-1 items-center">
          {value.map((segmentValue) => {
            const segment = segments.find((item) => item.value === segmentValue)

            return (
              <Badge
                key={segmentValue}
                title={segmentValue}
                variant="secondary"
              >
                {segment?.label ?? segmentValue}
                <button
                  aria-label={`Remove ${segment?.label ?? segmentValue}`}
                  className="ml-1 rounded-full outline-none ring-offset-background focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                  disabled={disabled}
                  onClick={() =>
                    onChange(removeSegmentValue(value, segmentValue))
                  }
                  onMouseDown={(event) => {
                    event.preventDefault()
                    event.stopPropagation()
                  }}
                  type="button"
                >
                  <X className="w-3 h-3 text-muted-foreground hover:text-foreground" />
                </button>
              </Badge>
            )
          })}

          <CommandPrimitive.Input
            className="flex-1 ml-2 bg-transparent outline-none placeholder:text-placeholder disabled:cursor-not-allowed disabled:opacity-50"
            disabled={disabled}
            onBlur={() => setOpen(false)}
            onFocus={() => setOpen(true)}
            onValueChange={(input) =>
              setInputValue(sanitizeSegmentInput(input))
            }
            placeholder={
              value.length === 0
                ? 'Choose or create segments for the organization'
                : undefined
            }
            ref={inputRef}
            value={inputValue}
          />
          {isLoading && (
            <CommandPrimitive.Loading>
              <Icons.spinner className="w-4 h-4 m-0" />
            </CommandPrimitive.Loading>
          )}
        </div>
      </div>
      {open &&
      !disabled &&
      (matchingSegments.length > 0 || showCreateSegment) ? (
        <div className="relative">
          <div className="absolute top-2 z-10 w-full border border-border border-primary rounded-md shadow-md outline-none bg-popover text-popover-foreground animate-in">
            <CommandGroup className="max-h-[350px] overflow-auto">
              {matchingSegments.map((segment) => (
                <CommandItem
                  className="cursor-pointer"
                  key={segment.value}
                  onMouseDown={(event) => {
                    event.preventDefault()
                    event.stopPropagation()
                  }}
                  onSelect={() => {
                    onChange(addSegmentValue(value, segment.value))
                    setInputValue('')
                  }}
                  value={`${segment.label} ${segment.value}`}
                >
                  {segment.label}
                </CommandItem>
              ))}
              {showCreateSegment && (
                <CommandItem
                  className="cursor-pointer"
                  disabled={createSegment.isPending}
                  onMouseDown={(event) => {
                    event.preventDefault()
                    event.stopPropagation()
                  }}
                  onSelect={handleCreateSegment}
                  value={`create-${inputValue.trim()}`}
                >
                  {createSegment.isPending && (
                    <Icons.spinner className="w-4 h-4 m-0" />
                  )}
                  Create segment {JSON.stringify(inputValue.trim())}
                </CommandItem>
              )}
            </CommandGroup>
          </div>
        </div>
      ) : null}
    </Command>
  )
}
